import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  Tab,
  Tabs,
  Typography,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import PendingActionsOutlinedIcon from '@mui/icons-material/PendingActionsOutlined';
import HistoryOutlinedIcon from '@mui/icons-material/HistoryOutlined';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import PageTitleIcon from '../../components/PageTitleIcon';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuthStore } from '../../stores/auth.store';
import { useOrganizationStore } from '../../stores/organization.store';
import { useAuthPermissions } from '../../hooks/useAuthPermissions';
import { canApproveRequests, canSubmitApprovals } from '../../auth';
import {
  approveApproval,
  cancelApproval,
  getApproval,
  listOrgApprovals,
  rejectApproval,
} from '../../services/approval.service';
import type { ApprovalRequestSchema } from '../../types/approval.type';
import ApprovalDetailDrawer from './ApprovalDetailDrawer';
import ApprovalsTable from './ApprovalsTable';

type TabValue = 'pending' | 'history';

const ApprovalsInbox: React.FC = () => {
  const { t } = useTranslation();
  const { tokenPayload, user } = useAuthStore();
  const { selectedOrganization } = useOrganizationStore();
  const { permissionList, isPlatformAdmin } = useAuthPermissions();
  const [searchParams, setSearchParams] = useSearchParams();

  const orgId = selectedOrganization?.id ?? null;

  const [tab, setTab] = useState<TabValue>('pending');
  const [items, setItems] = useState<ApprovalRequestSchema[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const currentUser = {
    sub: tokenPayload?.sub ?? null,
    id: user?.id ?? user?.sub ?? null,
    username: user?.username ?? tokenPayload?.username ?? null,
  };

  const canSubmit = isPlatformAdmin || (orgId ? canSubmitApprovals(permissionList, orgId) : false);
  const canApprove = isPlatformAdmin || (orgId ? canApproveRequests(permissionList, orgId) : false);

  const loadList = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    setError(null);
    try {
      const params =
        tab === 'pending'
          ? { status: 'pending' as const, limit: 50, offset: 0 }
          : { limit: 50, offset: 0 };
      const result = await listOrgApprovals(orgId, params);
      const list =
        tab === 'history'
          ? result.result_list.filter((r) => r.status !== 'pending')
          : result.result_list;
      setItems(list);
      setTotalCount(tab === 'history' ? list.length : result.total_count);
    } catch (err) {
      setError(err instanceof Error ? err.message : t('approvals.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [tab, orgId, t]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    const requestId = searchParams.get('request');
    if (requestId) {
      setSelectedId(requestId);
    }
  }, [searchParams]);

  const pendingCount = useMemo(
    () => (tab === 'pending' ? items.length : undefined),
    [tab, items.length],
  );

  const openDetail = (id: string) => {
    setSelectedId(id);
    setSearchParams({ request: id });
  };

  const closeDetail = () => {
    setSelectedId(null);
    setSearchParams({});
  };

  if (!orgId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="info">{t('approvals.selectOrganization')}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Paper
        elevation={2}
        sx={{ mb: 3, p: 3, borderRadius: '16px' }}
      >
        <Stack
          direction={{ xs: 'column', sm: 'row' }}
          justifyContent="space-between"
          alignItems={{ xs: 'flex-start', sm: 'center' }}
          spacing={2}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <PageTitleIcon icon={<FactCheckIcon />} />
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600, color: "text.primary" }}>
                {t('approvals.title')}
              </Typography>
              <Typography variant="body1" color="text.secondary" sx={{ mt: 0.5 }}>
                {t('approvals.subtitle', { name: selectedOrganization?.name })}
              </Typography>
            </Box>
          </Box>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadList}
            disabled={loading}
            sx={{ borderRadius: '12px' }}
          >
            {t('approvals.refresh')}
          </Button>
        </Stack>
      </Paper>

      <Paper
        elevation={2}
        sx={{ borderRadius: '16px', mb: 3, overflow: 'hidden' }}
      >
        <Tabs
          value={tab}
          onChange={(_, v) => setTab(v as TabValue)}
          sx={{
            px: 2,
            borderBottom: 1,
            borderColor: 'divider',
            '& .MuiTab-root': { minHeight: 56, fontWeight: 600, textTransform: 'none' },
          }}
        >
          <Tab
            icon={<PendingActionsOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label={
              <Stack direction="row" alignItems="center" spacing={1}>
                <span>{t('approvals.inbox')}</span>
                {pendingCount != null && pendingCount > 0 && (
                  <Chip label={pendingCount} size="small" color="warning" sx={{ height: 22 }} />
                )}
              </Stack>
            }
            value="pending"
          />
          <Tab
            icon={<HistoryOutlinedIcon fontSize="small" />}
            iconPosition="start"
            label={t('approvals.history')}
            value="history"
          />
        </Tabs>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <ApprovalsTable
        items={items}
        totalCount={totalCount}
        loading={loading}
        selectedId={selectedId}
        showOutcome={tab === 'history'}
        onRowClick={openDetail}
      />

      <ApprovalDetailDrawer
        open={Boolean(selectedId)}
        requestId={selectedId}
        organizationId={orgId}
        currentUser={currentUser}
        canApprove={canApprove}
        canSubmit={canSubmit}
        onClose={closeDetail}
        onRefresh={loadList}
        loadDetail={(id, oid) => getApproval(id, oid)}
        onApprove={(id, oid, notes) => approveApproval(id, { notes }, oid).then(() => undefined)}
        onReject={(id, oid, reason) =>
          rejectApproval(id, { rejection_reason: reason }, oid).then(() => undefined)
        }
        onCancel={(id, oid) => cancelApproval(id, oid).then(() => undefined)}
      />
    </Box>
  );
};

export default ApprovalsInbox;
